import { PLACEMENT } from '../analytic/constants.js';
import { addB2bAPIParams } from '../b2b';
import { PRE_ACTION, PRE_REQUEST_IDS } from '../constants/app';
import {
  FILTER_MOBILE_FULL_HEIGHT_SELECTOR,
  FILTER_MOBILE_WRAPPER_SELECTOR,
  FILTER_TREE_STATE_KEY,
} from '../constants/filter-tree.js';
import { renderRecommendationForSearchPage } from '../dynamicBundle/index.js';
import { collapseMobileBtn, getFilterSettings, lazyLoadFilterTree } from '../filter-tree';
import { handleDocumentClick } from '../filter-tree/events/action-on-click';
import { handleInCollectionSearch } from '../in-collection-search/index.js';
import { appIntegration } from '../integration/index.js';
import { transformProductPrice } from '../product-item';
import { restrictMainCurrency } from '../shopify-market/restrict-user';
import { getTemplateParsedById } from '../template/template-management';
import { handleTooltip } from '../tooltip/index.js';
import {
  addParamsLocale,
  buildQueryParams,
  getCustomerId,
  getLocalStorage,
  getQueryParamByKey,
  getThemeSettings,
  isMobile,
  isPageType,
  isSearchPage,
  isTabletPortraitMaxWidth,
  isVendorPage,
  parseFilterTreeToParams,
  setLocalStorage,
  setPaginationSession,
} from '../utils';

/**
 * @type {import('./index.d').initGetApi}
 */
export function initGetApi(context) {
  context.getFilterApi = async function (
    url,
    domId,
    addition = {
      responseType: 'json',
      modifyParams: null,
      additionParams: {},
    }
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    url = isSearchPage(context) || searchQuery ? context.app.searchUrl : url;

    // two class ref from main.liquid filter tree template please don't change two class.
    const classGlobalHide = 'boost-sd__g-hide';
    const idLoadingFilterIcon = 'boost-sd-loading-icon-filter';
    const loadingFilterTree = document.getElementById(idLoadingFilterIcon);

    // show filter loading icon => remove class hide
    if (loadingFilterTree) {
      loadingFilterTree.classList?.remove(classGlobalHide);
    }

    const { responseType = 'json', modifyParams, additionParams } = addition;
    let params = buildParams(context, additionParams, modifyParams);
    params = addParamsLocale(params);

    const queryParams = buildQueryParams(params);
    if (
      context.app.generalSettings.collection_id ||
      context.app.generalSettings.collection_id === 0
    ) {
      queryParams.set('collection_scope', context.app.generalSettings.collection_id);
      queryParams.set('tag', context.app.generalSettings?.collection_tags?.[0] || '');
    }

    if (context.app.b2b?.enabled) {
      addB2bAPIParams(queryParams, context);
    }

    const key = queryParams.toString();
    const hasFilterParams = key.includes('pf_');

    const cache = context.cache || {};
    const htmlCache = cache[key] ? cache[key] : {};
    const newId = window.boostWidgetIntegration.generateUuid();
    context.latestRequestKey = key;
    let { html = null } = htmlCache;
    const { filter = {}, bundles = [] } = htmlCache;

    if (!html) {
      queryParams.set('t', Date.now());
      queryParams.set('sid', window.boostWidgetIntegration.getSessionId());
      const { sortingAvailableFirst, availableAfterFiltering, productAndVariantAvailable } =
        getFilterSettings(context);

      // build filter setting - product list - Sort available products first
      if (sortingAvailableFirst) {
        queryParams.set('sort_first', 'available');
      }

      // // build filter setting - product list - Out of stock display options
      if (!availableAfterFiltering && !productAndVariantAvailable) {
        // show out of stock products/variants : availableAfterFiltering:false, productAndVariantAvailable: false
        queryParams.set('product_available', false);
        queryParams.set('variant_available', false);
      } else if (!availableAfterFiltering && productAndVariantAvailable) {
        //hide out of stock products/variants: availableAfterFiltering:false, productAndVariantAvailable:true
        queryParams.set('product_available', true);
        queryParams.set('variant_available', true);
      } else {
        //only hide out of stock products/variants after filtering: availableAfterFiltering: true  productAndVariantAvailable:false
        queryParams.set('product_available', hasFilterParams);
        queryParams.set('variant_available', hasFilterParams);
      }

      // handle custom parameters
      const customParams =
        window.boostSdCustomParams ||
        window.boostWidgetIntegration?.config?.customization?.filter?.customizedAPIParams;
      if (customParams && Object.keys(customParams).length > 0) {
        for (const key in customParams) {
          queryParams.set(key, customParams[key]);
        }
      }

      let response = null;
      const widgetId = queryParams.get('widgetId');
      const template = getTemplateParsedById(context, widgetId);
      if (template) {
        queryParams.delete('widgetId');
        response = await fetch(`${url}?${queryParams}`, {
          method: 'GET',
        });
        response = await toJson(response);

        const queryObject = {};
        for (const [key, value] of queryParams.entries()) {
          queryObject[key] = value;
        }

        response.html = await context.templateRender(template, {
          ...response,
          request: { ...queryObject, ...params },
        });
        // response.html = context.templateRender(template, {
        //   ...response,
        //   request: { ...queryObject, ...params },
        // });
      } else {
        response = await fetch(`${url}?${queryParams}`, {
          method: 'GET',
        });
      }

      if (responseType === 'json') {
        const resJson = await toJson(response);

        html = await resJson.html;

        const requestId = await resJson.meta?.rid;
        const action = isSearchPage() ? 'search' : 'filter';
        saveRequestId(action, requestId, resJson.bundles);
        setLocalStorage(PRE_ACTION, action);

        context.state.filterTree = {
          ...resJson.filter,
          optionsMap: resJson.filter?.options?.reduce(
            (obj, val) => ({ ...obj, [val.filterOptionId]: val }),
            {}
          ),
        };

        context.dynamicBundles = resJson?.bundles;

        const timestamp = new Date().getTime();
        context.cache[key] = {
          ...resJson,
          html,
          timestamp,
        };

        const maxCacheSize = 50;
        if (Object.keys(cache).length >= maxCacheSize) {
          const sortedKeys = Object.keys(cache).sort(
            (a, b) => cache[a].timestamp - cache[b].timestamp
          );
          delete cache[sortedKeys[0]];
        }

        if (resJson.meta) {
          const { money_format, money_format_with_currency, currency } = resJson.meta;
          restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
        }
      }
    } else {
      context.state.filterTree = filter;
      context.dynamicBundles = bundles;
    }

    // replace default class none or display none in mobile from second call
    if (html) {
      const {
        filterLayout,
        filterTreeVerticalStyle,
        filterTreeMobileStyle,
        filterTreeHorizontalStyle,
      } = getFilterSettings(context);
      const [getFilterTreeDesktopOpening, setFilterTreeDesktopOpening] = context.useContextState(
        FILTER_TREE_STATE_KEY.DESKTOP_OPENING,
        false
      );

      // Update status filter tree toggle button icon
      const isMobileSize = isMobile(context?.app?.generalSettings?.isTabletPortraitMax);
      const filterTreeMobileWrapper = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
      if (
        isMobileSize &&
        filterTreeMobileStyle === 'style1' &&
        filterTreeMobileWrapper?.style?.display !== 'none'
      ) {
        html = html.replace(
          'class="boost-sd__filter-tree-toggle-button"',
          'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--active"'
        );
      }

      if (
        !isMobileSize &&
        filterTreeVerticalStyle !== 'style-default' &&
        filterLayout === 'vertical' &&
        getFilterTreeDesktopOpening()
      ) {
        html = html.replace(
          'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--expand"',
          'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--expand boost-sd__filter-tree-toggle-button--active"'
        );
      }

      if (filterTreeVerticalStyle === 'style-off-canvas' && getFilterTreeDesktopOpening()) {
        // remove class display none
        html = html.replace(
          'class="boost-sd__filter-tree-vertical-sticky-overlay boost-sd__filter-tree-vertical--hidden"',
          'class="boost-sd__filter-tree-vertical-sticky-overlay"'
        );
      }

      if (filterTreeVerticalStyle === 'style-expand' && getFilterTreeDesktopOpening()) {
        // remove class display none
        html = html.replace(
          'class="boost-sd__filter-tree-vertical  boost-sd__filter-tree-vertical--hidden',
          'class="boost-sd__filter-tree-vertical'
        );
      }

      if (filterLayout === 'horizontal' && filterTreeHorizontalStyle === 'style-expand') {
        setFilterTreeDesktopOpening(false);
      }
    }

    if (context.latestRequestKey === key && domId) {
      const isAppendToProductListType =
        additionParams?.behavior === 'more' || additionParams?.behavior === 'previous';
      const { filterTreeMobileStyle, filterTreeVerticalStyle, filterLayout } =
        getFilterSettings(context);
      const filterTreeMobileWrapper = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);

      if (isAppendToProductListType) {
        // handle append/prepend to product list instead of replacing the whole page html
        handleProductListHTML(context, html, additionParams?.behavior);
      } else {
        const oldDOM = document.getElementById(domId);
        const nextDOM = oldDOM?.cloneNode(false);
        context.isRendering = true;

        if (!context.onAfterDocumentChanged) {
          context.onAfterDocumentChanged = function () {
            if (isSearchPage()) {
              renderRecommendationForSearchPage(context);
              import(
                /* webpackChunkName: 'volume-bundle' */
                '../volume-bundle'
              ).then((module) => {
                const { renderVolumeBundleForSearchPage } = module;
                renderVolumeBundleForSearchPage(context);
              });
            }
            appIntegration();
            handleTooltip(context);
          };
        }

        handleReplaceHtml(context, () => {
          if (nextDOM) {
            nextDOM.innerHTML = html;
            context.document = nextDOM;
            context.document?.addEventListener('click', function (event) {
              handleDocumentClick(context, event);
            });
            // Re-attach event listeners to the new DOM
            handleInCollectionSearch(context, nextDOM);
            context.oldDOM = oldDOM;
          }
        });

        setPaginationSession(getQueryParamByKey('page'));
      }
      if (responseType === 'json') context.state.latestFilterSearchRequest = newId;
      const { paginationType = 'default' } =
        getThemeSettings(context)?.additionalElements?.pagination || {};

      if (paginationType === 'infinite_scroll') {
        // avoid infinite scroll firing multiple times when new page is still loading
        const [, setIsLoading] = context.useContextState('pagination-infinite-loading ', false);
        setIsLoading(false);
      }

      // if filter tree is currently open on mobile and not in style 1 - expand, -> keep no scroll class
      // otherwise, remove no scroll on body -> remove class
      const isMobileVisible =
        filterTreeMobileWrapper?.style?.display !== 'none' &&
        filterTreeMobileStyle !== 'style1' &&
        isTabletPortraitMaxWidth();

      const isDesktopOffCanvas =
        !isTabletPortraitMaxWidth() &&
        filterTreeVerticalStyle === 'style-off-canvas' &&
        filterLayout === 'vertical';

      if (!(isMobileVisible || isDesktopOffCanvas)) {
        document.body.classList.remove('boost-sd__g-no-scroll');
      }

      // remove filter tree loading -> add class hide
      if (loadingFilterTree) {
        loadingFilterTree.classList.add(classGlobalHide);
      }

      return html;
    }
  };
}

/**
 * @type {import('./index.d').toJson}
 */
async function toJson(res) {
  try {
    return await res.json();
  } catch (e) {
    return res;
  }
}

/**
 * @type {import('./index.d').buildParams}
 */
export const buildParams = (context, additionParams, modifyParams) => {
  const [getFilter] = context.useContextState('filter', []);
  const [getSort] = context.useContextState('sort', {});
  const [getPage] = context.useContextState('pagination ', {});
  const [getFilterToggleButton] = context.useContextState('filter-toggle-button', {});
  const filterParams = parseFilterTreeToParams(getFilter());
  const [getFilterTreeOption] = context.useContextState('filterTree', {});
  const options = getFilterTreeOption().options;

  let params = { ...context.defaultParams, ...filterParams };
  // build and condition
  options?.forEach((option) => {
    if (option.useAndCondition) {
      params[`${option.filterOptionId}_and_condition`] = true;
    }
  });

  if (getSort && getSort().sort) {
    params['sort'] = getSort().sort;
  }
  if (getPage && getPage().page) {
    params['page'] = getPage().page;
  }
  params['isMobile'] = isMobile(context.app.generalSettings.isTabletPortraitMax || 575);
  params['isTabletPortraitMax'] = isMobile(context.app.generalSettings.isTabletPortraitMax);
  params['first_load'] = false;
  params['customer_id'] = getCustomerId();

  if (additionParams) {
    params = { ...params, ...additionParams };
  }
  if (getFilterToggleButton() && getFilterToggleButton().showFilterTree) {
    params['showFilterTree'] = true;
  }

  if (params.pf_tag) {
    params.tag = params.pf_tag;
    delete params.pf_tag;
  }

  // add param filter when filter has param
  if (Object.keys(filterParams).length !== 0) {
    params.filter = true;
  } else {
    delete params.filter;
  }

  if (isVendorPage()) {
    const urlObj = new URL(window.location.href);
    const q = urlObj.searchParams.get('q');

    if (q) {
      params.vendor = q;
    }
  } else if (isPageType()) {
    const urlObj = new URL(window.location.href);
    const q = urlObj.searchParams.get('q');

    if (q) {
      params.product_type = q;
    }
  }

  return modifyParams ? modifyParams(params) : params;
};

/**
 * @type {import('./index.d').getTemplateById}
 */
export const getTemplateById = async (context, params) => {
  const url = context.app.templateUrl;

  const response = await fetch(
    `${url}?${buildQueryParams({ ...context.defaultParams, ...params })}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

/**
 * @type {import('./index.d').saveRequestId}
 */
export function saveRequestId(type = 'filter', request_id, bundles = []) {
  if (!request_id) return;

  const requestIds = getLocalStorage(PRE_REQUEST_IDS) || {};
  requestIds[type] = request_id;

  if (bundles && Array.isArray(bundles)) {
    bundles.forEach((bundle) => {
      const placement =
        type === 'search'
          ? PLACEMENT.search_page
          : type === 'suggest'
            ? PLACEMENT.instant_search
            : PLACEMENT.product_page;
      requestIds[`${bundle.widgetId}_${placement}`] = request_id;
    });
  }

  setLocalStorage(PRE_REQUEST_IDS, requestIds);
}

/**
 * @type {import('./index.d').handleReplaceHtml}
 */
const handleReplaceHtml = async (context, replaceFn) => {
  let mobile = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
  if (mobile?.style && mobile?.style?.display !== 'none') {
    const fullHeight = context.$(FILTER_MOBILE_FULL_HEIGHT_SELECTOR, mobile);
    if (fullHeight) {
      const fullHeightHtml = fullHeight.innerHTML;
      const fullHeightId = fullHeight.id;
      replaceFn();
      mobile = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
      collapseMobileBtn(context, { elementId: fullHeightId });
      if (context.$(`#${fullHeightId}`, mobile)) {
        context.$(`#${fullHeightId}`, mobile).innerHTML = fullHeightHtml;
      }
    } else {
      replaceFn();
    }

    if (context.$(FILTER_MOBILE_WRAPPER_SELECTOR)) {
      context.$(FILTER_MOBILE_WRAPPER_SELECTOR).style.display = 'unset';
    }

    await lazyLoadFilterTree(context);
  } else {
    replaceFn();
  }
};

/**
 * @type {import('./index.d').handleProductListHTML}
 */
const handleProductListHTML = (context, html, behavior) => {
  const productList = context.document.querySelector('.boost-sd__product-list');
  html = html?.replace(/loading="lazy"/g, '');

  // remove outer div of new page html product list
  const outerDiv = document.createElement('div');
  outerDiv.innerHTML = html;

  const innerContent = outerDiv?.querySelector('.boost-sd__product-list');
  const modifiedHtml = innerContent?.innerHTML;

  if (behavior === 'previous') {
    const firstProductItem = productList.querySelector('.boost-sd__product-item');
    firstProductItem && firstProductItem.insertAdjacentHTML('beforebegin', modifiedHtml);
  } else if (behavior === 'more') {
    const lastProductItem = productList.querySelector('.boost-sd__product-item:last-child');
    lastProductItem && lastProductItem.insertAdjacentHTML('afterend', modifiedHtml);
  }

  transformProductPrice(context);
};
